import { useMemo, useRef } from "react";

import { useFeatureFlagFromFirestore } from "../../containers/streaming/useFeatureFlagFromFirestore";
import { dbFirestore } from "../../firestore";
import { useSelector } from "../../store";
import { convertDateToTimestamp } from "../../utility/firebase";

import type { UserMessage } from "@spwn/types/firebase/firestore";

/**
 * feature flag のゲートはここに置く。呼び出し側で UI だけ隠すと書き込み経路は
 * 生きたままになるが、ここで undefined を返せば UI と書き込みが同時に閉じる。
 */
export const useBlockUser = ({ eventId }: { eventId: string | undefined }) => {
  const userId = useSelector((state) => state.auth.user.uid);
  const isEnabled = useFeatureFlagFromFirestore(
    "commentPersonalBlock",
    eventId,
    { enable: "commentPersonalBlock", disable: "noCommentPersonalBlock" }
  );
  const tombstoneCommentIdsRef = useRef<Set<string>>(new Set());

  const blockUser = useMemo(() => {
    // フラグ取得前は undefined が返る。ロールアウト用のゲートなので既定はオフ扱いにし、
    // 「一瞬だけボタンが出て消える」ことがないようにする。
    if (isEnabled !== true) return undefined;
    // auth.user の初期値は `{} as any` なので、uid は型に反して未ログイン時 undefined。
    if (!userId || !eventId) return undefined;

    return async (
      comment: Pick<UserMessage, "uid" | "name">,
      shownComments: readonly Pick<UserMessage, "_id" | "uid">[]
    ) => {
      // 書き込みの完了を待つ前に確定させる。await した後だと、書き込み中に届いた同じ
      // ユーザーの新規コメントまで「ブロック時に出ていた」ことになり墓標が増える。
      for (const shownComment of shownComments) {
        if (
          shownComment.uid === comment.uid &&
          shownComment._id !== undefined
        ) {
          tombstoneCommentIdsRef.current.add(shownComment._id);
        }
      }
      // 監査や他ユーザーとの順序整合には使わず、自分専用リストの表示と並び順にしか
      // 使わないため、portal の他の timestamp と違いサーバー時刻にしない。
      await dbFirestore
        .blockedUsers(userId)
        .doc(comment.uid)
        .set({
          createdAt: convertDateToTimestamp(new Date()),
          name: comment.name ?? "",
          eventId,
        });
    };
  }, [isEnabled, userId, eventId]);

  const unblockUser = useMemo(() => {
    if (isEnabled !== true) return undefined;
    if (!userId) return undefined;

    return async (blockedUid: string) => {
      // 墓標にした _id は消さない。applyBlockedComments は blockedUserIds に居る
      // 間だけ墓標に差し替えるので、doc を消せば元のコメントがそのまま戻る。
      await dbFirestore.blockedUsers(userId).doc(blockedUid).delete();
    };
  }, [isEnabled, userId]);

  return {
    blockUser,
    unblockUser,
    tombstoneCommentIds: tombstoneCommentIdsRef.current,
  };
};
